import * as React from "react";
import {
    Box,
    Collapse,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
    Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import type { Category } from "../../models/Category";
import { filterTree, getPath, isSameOrDescendant, type CategoryNode } from "./categoryTree.utils";

type CategoryTreeViewProps = {
    tree: CategoryNode[];
    categories: Category[];
    selectedId: number | null;
    onSelect: (id: number) => void;
    onAddRoot: () => void;
    onAddChild: (parentId: number) => void;
    onRequestDelete: (id: number) => void;
    onMove: (id: number, newParentId: number | null) => void;
};

function subtreeContains(node: CategoryNode, candidateId: number): boolean {
    if (node.id === candidateId) return true;
    return node.children.some((c) => subtreeContains(c, candidateId));
}

export default function CategoryTreeView({
    tree,
    categories,
    selectedId,
    onSelect,
    onAddRoot,
    onAddChild,
    onRequestDelete,
    onMove,
}: CategoryTreeViewProps) {
    const [query, setQuery] = React.useState("");
    const [expanded, setExpanded] = React.useState<Set<number>>(new Set());
    const [draggingId, setDraggingId] = React.useState<number | null>(null);
    const [dropTargetId, setDropTargetId] = React.useState<number | null | "root">(null);

    React.useEffect(() => {
        if (selectedId == null) return;
        const path = getPath(categories, selectedId);
        if (!path.length) return;
        setExpanded((prev) => {
            const next = new Set(prev);
            for (const n of path) next.add(n.id);
            return next;
        });
    }, [selectedId, categories]);

    const { tree: visibleTree, expandIds: searchExpandIds } = React.useMemo(
        () => filterTree(tree, query),
        [tree, query]
    );

    const isExpanded = (id: number) => expanded.has(id) || searchExpandIds.has(id);

    const toggleExpanded = (id: number) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleDrop = (targetId: number | null) => {
        if (draggingId == null) return;
        const draggedCategory = categories.find((c) => c.id === draggingId);
        // System categories can't be reparented from this page.
        if (!draggedCategory || draggedCategory.clientId == null) {
            setDraggingId(null);
            setDropTargetId(null);
            return;
        }
        if (targetId != null) {
            const draggedNode = findNode(tree, draggingId);
            if (draggedNode && subtreeContains(draggedNode, targetId)) {
                setDraggingId(null);
                setDropTargetId(null);
                return;
            }
            if (isSameOrDescendant(categories, draggingId, targetId)) {
                setDraggingId(null);
                setDropTargetId(null);
                return;
            }
        }
        if (draggedCategory.parentId !== targetId) {
            onMove(draggingId, targetId);
        }
        setDraggingId(null);
        setDropTargetId(null);
    };

    return (
        <Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search categories…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: query ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setQuery("")} aria-label="Clear search">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined,
                        },
                    }}
                />
                <Tooltip title="Add top-level category">
                    <IconButton color="primary" onClick={onAddRoot} aria-label="Add top-level category">
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {draggingId != null && (
                <Box
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDropTargetId("root");
                    }}
                    onDragLeave={() => setDropTargetId((t) => (t === "root" ? null : t))}
                    onDrop={(e) => {
                        e.preventDefault();
                        handleDrop(null);
                    }}
                    sx={{
                        border: "1px dashed",
                        borderColor: dropTargetId === "root" ? "primary.main" : "divider",
                        borderRadius: 1,
                        p: 1,
                        mb: 1,
                        textAlign: "center",
                        color: "text.secondary",
                        bgcolor: dropTargetId === "root" ? "action.hover" : "transparent",
                    }}
                >
                    <Typography variant="caption">Drop here to move to top level</Typography>
                </Box>
            )}

            {visibleTree.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {query ? "No categories match your search." : "No categories yet — add one to get started."}
                </Typography>
            ) : (
                <List dense disablePadding role="tree">
                    {visibleTree.map((node) => (
                        <CategoryTreeRow
                            key={node.id}
                            node={node}
                            depth={0}
                            selectedId={selectedId}
                            isExpanded={isExpanded}
                            toggleExpanded={toggleExpanded}
                            onSelect={onSelect}
                            onAddChild={onAddChild}
                            onRequestDelete={onRequestDelete}
                            draggingId={draggingId}
                            dropTargetId={dropTargetId}
                            setDraggingId={setDraggingId}
                            setDropTargetId={setDropTargetId}
                            handleDrop={handleDrop}
                        />
                    ))}
                </List>
            )}
        </Box>
    );
}

function findNode(nodes: CategoryNode[], id: number): CategoryNode | null {
    for (const n of nodes) {
        if (n.id === id) return n;
        const found = findNode(n.children, id);
        if (found) return found;
    }
    return null;
}

type RowProps = {
    node: CategoryNode;
    depth: number;
    selectedId: number | null;
    isExpanded: (id: number) => boolean;
    toggleExpanded: (id: number) => void;
    onSelect: (id: number) => void;
    onAddChild: (parentId: number) => void;
    onRequestDelete: (id: number) => void;
    draggingId: number | null;
    dropTargetId: number | null | "root";
    setDraggingId: React.Dispatch<React.SetStateAction<number | null>>;
    setDropTargetId: React.Dispatch<React.SetStateAction<number | null | "root">>;
    handleDrop: (targetId: number | null) => void;
};

function CategoryTreeRow({
    node,
    depth,
    selectedId,
    isExpanded,
    toggleExpanded,
    onSelect,
    onAddChild,
    onRequestDelete,
    draggingId,
    dropTargetId,
    setDraggingId,
    setDropTargetId,
    handleDrop,
}: RowProps) {
    const [hovered, setHovered] = React.useState(false);
    const hasChildren = node.children.length > 0;
    const expanded = isExpanded(node.id);
    const selected = selectedId === node.id;
    const isDropTarget = dropTargetId === node.id;
    const isBeingDragged = draggingId === node.id;
    const isSystem = node.clientId == null;

    return (
        <>
            <ListItemButton
                dense
                selected={selected}
                onClick={() => onSelect(node.id)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                draggable={!isSystem}
                onDragStart={(e) => {
                    e.stopPropagation();
                    setDraggingId(node.id);
                }}
                onDragEnd={() => {
                    setDraggingId(null);
                    setDropTargetId(null);
                }}
                onDragOver={(e) => {
                    if (draggingId == null || draggingId === node.id) return;
                    e.preventDefault();
                    e.stopPropagation();
                    setDropTargetId(node.id);
                }}
                onDragLeave={() => setDropTargetId((t) => (t === node.id ? null : t))}
                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDrop(node.id);
                }}
                sx={{
                    pl: 1 + depth * 2.5,
                    opacity: isBeingDragged ? 0.4 : 1,
                    bgcolor: isDropTarget ? "action.hover" : undefined,
                    outline: isDropTarget ? "1px dashed" : "none",
                    outlineColor: "primary.main",
                    borderRadius: 1,
                }}
            >
                <Box
                    sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center", mr: 0.5 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (hasChildren) toggleExpanded(node.id);
                    }}
                >
                    {hasChildren ? (
                        expanded ? (
                            <ExpandMoreIcon fontSize="small" />
                        ) : (
                            <ChevronRightIcon fontSize="small" />
                        )
                    ) : null}
                </Box>

                {isSystem ? (
                    <Tooltip title="System category — can't be dragged">
                        <LockOutlinedIcon
                            fontSize="small"
                            sx={{ color: "text.disabled", mr: 0.5, visibility: hovered ? "visible" : "hidden" }}
                        />
                    </Tooltip>
                ) : (
                    <DragIndicatorIcon
                        fontSize="small"
                        sx={{ color: "text.disabled", mr: 0.5, cursor: "grab", visibility: hovered ? "visible" : "hidden" }}
                    />
                )}

                <ListItemText primary={node.name} slotProps={{ primary: { noWrap: true } }} />

                {isSystem && (
                    <Chip
                        label="System"
                        size="small"
                        variant="outlined"
                        sx={{ mr: 1, display: hovered || selected ? "inline-flex" : { xs: "inline-flex", sm: "none" } }}
                    />
                )}

                <Box sx={{ display: "flex", gap: 0.25, visibility: hovered || selected ? "visible" : "hidden" }}>
                    <Tooltip title="Add child category">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddChild(node.id);
                            }}
                            aria-label={`Add child of ${node.name}`}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {!isSystem && (
                        <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRequestDelete(node.id);
                                }}
                                aria-label={`Delete ${node.name}`}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </ListItemButton>

            {hasChildren && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List dense disablePadding role="group">
                        {node.children.map((child) => (
                            <CategoryTreeRow
                                key={child.id}
                                node={child}
                                depth={depth + 1}
                                selectedId={selectedId}
                                isExpanded={isExpanded}
                                toggleExpanded={toggleExpanded}
                                onSelect={onSelect}
                                onAddChild={onAddChild}
                                onRequestDelete={onRequestDelete}
                                draggingId={draggingId}
                                dropTargetId={dropTargetId}
                                setDraggingId={setDraggingId}
                                setDropTargetId={setDropTargetId}
                                handleDrop={handleDrop}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
}
